import React, { useEffect } from 'react';

import gql from 'graphql-tag';
import { useLazyQuery } from 'react-apollo';

import { useI18n, useSelector } from 'Hooks';
import CreateModificationGroup from 'Scenes-redesign/PIM/Components/CreateContent/ModificationGroup';

import { GetCompanyData, GetCompanyDataVariables } from './__queries__';
import { CreateNewWrapper } from './styles';
import { getItems, getItemsVariables } from '../../../Scenes-redesign/PIM/__queries__';
import CreateUpsell from '../../../Scenes-redesign/PIM/Components/CreateContent/Upsell';
import { ITEMS_QUERY } from '../../../Scenes-redesign/PIM/queries';
import { getIngredientsList } from '../../../Scenes-redesign/PIM/utils';
import AddButton from '../../AddButton';
import useModal from '../../Modal/useModal';

export const COMPANY_QUERY = gql`
  query GetCompanyData($companyId: ID!) {
    company: Company(id: $companyId) {
      id
      hasKDS
      variations {
        id
        name
        price
        order
      }
      upsells {
        id
        displayOrder
        name {
          id
          en
          se
          es
          no
        }
        items {
          id
          title
        }
      }
      modificationGroups {
        id
        internalName
        externalName
        type
        expandedByDefault
        variations {
          id
          name
        }
      }
    }
  }
`;

interface Props {
  formId: string;
  addNew: (node) => void;
}

const CreateNew = ({ formId, addNew }: Props) => {
  const companyId = useSelector((state) => state.company);
  if (!companyId) return null;
  const { isShowing, toggle, hide } = useModal();
  const { t } = useI18n();

  const [getCompanyData, { data }] = useLazyQuery<GetCompanyData, GetCompanyDataVariables>(
    COMPANY_QUERY,
    {
      variables: { companyId },
    },
  );

  const [getItems, { data: allItems }] = useLazyQuery<getItems, getItemsVariables>(ITEMS_QUERY, {
    variables: { companyId },
  });

  useEffect(() => {
    switch (formId) {
      case 'variations':
        getCompanyData();
        break;

      case 'upsales':
        getItems();
        getCompanyData();
        break;

      default:
        break;
    }
  }, [formId]);

  const variationsList = data?.company ? getIngredientsList(data?.company?.variations) : [];
  const upsellsList = data?.company ? data.company.upsells : [];

  const handleAddSuccess = (node) => {
    addNew(node);
    hide();
  };

  const getContent = () => {
    switch (formId) {
      case 'upsales':
        return (
          <CreateNewWrapper>
            <AddButton text={t('General.AddUpsellGroup')} handleClick={toggle} />
            {isShowing && (
              <CreateUpsell
                items={allItems?.getItems?.results ?? []}
                upsells={upsellsList ?? []}
                closeModal={hide}
                handleFormSuccess={(node) => handleAddSuccess(node)}
              />
            )}
          </CreateNewWrapper>
        );

      case 'modification-group':
        return (
          <CreateNewWrapper>
            <AddButton text={t('General.AddGroup')} handleClick={toggle} />
            {isShowing && (
              <CreateModificationGroup
                variationsList={variationsList ?? []}
                isShowing={isShowing}
                closeModal={hide}
                handleFormSuccess={(data) => handleAddSuccess(data.node)}
              />
            )}
          </CreateNewWrapper>
        );

      default:
        return null;
    }
  };

  return getContent();
};

export default CreateNew;
